import { OriginalPetForm } from '../types';

export interface OriginalPetAsset {
  id: OriginalPetForm;
  name: string;
  image: string;
  idleSheet?: string;
  idleFrameCount?: number;
  stage: 'egg' | 'baby' | 'branch' | 'final';
  branch?: 'star' | 'cloud' | 'flame';
  evolvesFrom?: OriginalPetForm;
}

const ASSET_ROOT = '/assets/virtual-pet/evolution';

export type EggAnimationState = 'idle' | 'shake' | 'touch' | 'sleep' | 'ready' | 'hatch';
export type PetActionState = 'idle' | 'eat' | 'play' | 'sleep';

export interface PetSpriteAnimation {
  src: string;
  frameCount: number;
  durationMs: number;
  loop: boolean;
}

export const STAR_EGG_ANIMATIONS: Record<EggAnimationState, PetSpriteAnimation> = {
  idle: { src: `${ASSET_ROOT}/idle/star-egg-idle.png`, frameCount: 4, durationMs: 2800, loop: true },
  shake: { src: `${ASSET_ROOT}/egg/star-egg-shake.png`, frameCount: 6, durationMs: 900, loop: true },
  touch: { src: `${ASSET_ROOT}/egg/star-egg-touch.png`, frameCount: 4, durationMs: 900, loop: true },
  sleep: { src: `${ASSET_ROOT}/egg/star-egg-sleep.png`, frameCount: 4, durationMs: 3200, loop: true },
  ready: { src: `${ASSET_ROOT}/egg/star-egg-ready.png`, frameCount: 6, durationMs: 1800, loop: true },
  hatch: { src: `${ASSET_ROOT}/egg/star-egg-hatch.png`, frameCount: 8, durationMs: 1700, loop: false }
};

const makeActionAnimations = (folder: string): Partial<Record<PetActionState, PetSpriteAnimation>> => ({
  eat: { src: `${ASSET_ROOT}/actions/${folder}/${folder}-eat.png`, frameCount: 4, durationMs: 1000, loop: true },
  play: { src: `${ASSET_ROOT}/actions/${folder}/${folder}-play.png`, frameCount: 6, durationMs: 900, loop: true },
  sleep: { src: `${ASSET_ROOT}/actions/${folder}/${folder}-sleep.png`, frameCount: 4, durationMs: 3200, loop: true }
});

export const PET_ACTION_ANIMATIONS: Partial<Record<OriginalPetForm, Partial<Record<PetActionState, PetSpriteAnimation>>>> = {
  mongsil: makeActionAnimations('mongsil'),
  starBean: makeActionAnimations('star-bean'),
  cloudBean: makeActionAnimations('cloud-bean'),
  flameBean: makeActionAnimations('flame-bean'),
  starKeeper: makeActionAnimations('star-keeper'),
  cloudGuardian: makeActionAnimations('cloud-guardian'),
  flameDragon: makeActionAnimations('flame-dragon')
};

export const ORIGINAL_PET_ASSETS: Record<OriginalPetForm, OriginalPetAsset> = {
  starEgg: {
    id: 'starEgg',
    name: '별알',
    image: `${ASSET_ROOT}/star-egg.png`,
    stage: 'egg'
  },
  mongsil: {
    id: 'mongsil',
    name: '몽실',
    image: `${ASSET_ROOT}/mongsil.png`,
    stage: 'baby',
    evolvesFrom: 'starEgg'
  },
  starBean: {
    id: 'starBean',
    name: '별콩',
    image: `${ASSET_ROOT}/star-bean.png`,
    stage: 'branch',
    branch: 'star',
    evolvesFrom: 'mongsil'
  },
  cloudBean: {
    id: 'cloudBean',
    name: '구름콩',
    image: `${ASSET_ROOT}/cloud-bean.png`,
    stage: 'branch',
    branch: 'cloud',
    evolvesFrom: 'mongsil'
  },
  flameBean: {
    id: 'flameBean',
    name: '불꽃콩',
    image: `${ASSET_ROOT}/flame-bean.png`,
    stage: 'branch',
    branch: 'flame',
    evolvesFrom: 'mongsil'
  },
  starKeeper: {
    id: 'starKeeper',
    name: '별지기',
    image: `${ASSET_ROOT}/star-keeper.png`,
    stage: 'final',
    branch: 'star',
    evolvesFrom: 'starBean'
  },
  cloudGuardian: {
    id: 'cloudGuardian',
    name: '구름수호자',
    image: `${ASSET_ROOT}/cloud-guardian.png`,
    stage: 'final',
    branch: 'cloud',
    evolvesFrom: 'cloudBean'
  },
  flameDragon: {
    id: 'flameDragon',
    name: '불꽃용',
    image: `${ASSET_ROOT}/flame-dragon.png`,
    stage: 'final',
    branch: 'flame',
    evolvesFrom: 'flameBean'
  }
};

Object.values(ORIGINAL_PET_ASSETS).forEach((asset) => {
  const fileName = asset.image.split('/').pop()?.replace('.png', '-idle.png') || '';
  asset.idleSheet = `${ASSET_ROOT}/idle/${fileName}`;
  asset.idleFrameCount = 4;
});

export const ORIGINAL_PET_EVOLUTION_PATHS: OriginalPetForm[][] = [
  ['starEgg', 'mongsil', 'starBean', 'starKeeper'],
  ['starEgg', 'mongsil', 'cloudBean', 'cloudGuardian'],
  ['starEgg', 'mongsil', 'flameBean', 'flameDragon']
];
